import { useState } from "react";

import { Link, useSearchParams } from "react-router-dom";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  Activity,
  ArrowRight,
  Ban,
  BookOpenCheck,
  CalendarClock,
  CheckCircle2,
  Clock,
  Filter,
  Flag,
  Globe,
  Languages,
  ListPlus,
  Percent,
  Send,
  Sparkles,
  Target,
  Timer,
  TrendingUp,
  Trophy,
  UserCheck,
  UserMinus,
  UserPlus,
  UserX,
  Users,
  XCircle,
} from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { adminApi } from "@/api/endpoints";
import { DashboardFilters } from "@/components/dashboard/dashboard-filters";
import { Donut } from "@/components/dashboard/donut";
import { StripChart } from "@/components/dashboard/strip-chart";
import { TrendChart } from "@/components/dashboard/trend-chart";
import { AnimatedNumber } from "@/components/shared/animated-number";
import { DeltaBadge } from "@/components/shared/delta-badge";
import { ErrorState } from "@/components/shared/error-state";
import { Logo } from "@/components/shared/logo";
import { Panel } from "@/components/shared/panel";
import { ProgressRing } from "@/components/shared/progress-ring";
import { RememberParams } from "@/components/shared/remember-params";
import { StatTile } from "@/components/shared/stat-tile";
import { UserAvatar } from "@/components/shared/user-avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { currentLanguage } from "@/configs/i18n";
import { queryKeys } from "@/configs/query-keys";
import {
  type ChannelSummary,
  type DashboardParams,
  funnelSteps,
  readDashboardParams,
  splitMinutes,
  summarizeDeliveries,
  todayIn,
} from "@/lib/dashboard";
import { displayName, formatNumber, formatShortDay, percent } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/auth";
import type { AdminDashboard, SeriesPoint } from "@/types/api";

const REMEMBERED = ["from", "to", "lang", "channel", "active"] as const;

export function DashboardPage() {
  return (
    <RememberParams table="dashboard" keys={REMEMBERED}>
      <DashboardView />
    </RememberParams>
  );
}

function DashboardView() {
  const [params] = useSearchParams();
  const today = todayIn();
  const filters = readDashboardParams(params, today);
  const dashboard = useQuery({
    queryKey: queryKeys.dashboard(filters),
    queryFn: () => adminApi.dashboard(filters),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <Hero filters={filters} fetching={dashboard.isFetching && !dashboard.isPending} />
      <DashboardFilters value={filters} today={today} />
      {dashboard.isPending ? (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-32 rounded-2xl" />
          ))}
        </div>
      ) : dashboard.isError ? (
        <ErrorState error={dashboard.error} onRetry={() => void dashboard.refetch()} />
      ) : (
        <DashboardContent data={dashboard.data} filters={filters} />
      )}
    </>
  );
}

/** Salomlashuv bandi: to'q fonda nishon halqalari aylanadi — logotipning o'zi. */
function Hero({ filters, fetching }: { filters: DashboardParams; fetching: boolean }) {
  const { t } = useTranslation();
  const lang = currentLanguage();
  const user = useAuthStore(s => s.user);

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="bg-brand-panel text-brand-panel-foreground relative shrink-0 overflow-hidden rounded-3xl p-6 sm:p-7"
    >
      <span
        aria-hidden
        className="bg-primary/40 animate-drift absolute -top-16 -left-10 size-56 rounded-full blur-3xl"
      />
      <span
        aria-hidden
        className="bg-tile-sky-strong/30 animate-drift absolute -right-10 -bottom-20 size-64 rounded-full blur-3xl [animation-delay:-6s]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/2 right-6 hidden -translate-y-1/2 sm:block"
      >
        <div className="animate-orbit relative size-40 rounded-full border border-dashed border-current opacity-20">
          <span className="bg-primary absolute -top-1.5 left-1/2 size-3 rounded-full" />
        </div>
        <div className="animate-orbit absolute inset-6 rounded-full border border-current opacity-25 [animation-direction:reverse] [animation-duration:12s]">
          <span className="bg-brand-accent absolute top-1/2 -right-1 size-2 rounded-full" />
        </div>
        <Logo className="absolute inset-0 m-auto size-12 drop-shadow-xl" />
      </div>

      <div className="relative max-w-xl space-y-2">
        <p className="bg-brand-panel-foreground/10 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium backdrop-blur">
          <Sparkles
            className={cn("text-brand-accent size-3.5", fetching && "animate-spin")}
            aria-hidden
          />
          {formatShortDay(filters.from, lang)} — {formatShortDay(filters.to, lang)}
        </p>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          {t("dashboard.title")}
        </h1>
        <p className="text-brand-panel-foreground/70 text-sm">
          {user?.firstName
            ? t("dashboard.greeting", { name: user.firstName })
            : t("dashboard.subtitle")}
        </p>
      </div>
    </motion.section>
  );
}

function DashboardContent({ data, filters }: { data: AdminDashboard; filters: DashboardParams }) {
  const { t } = useTranslation();
  const { users, tasks, previous } = data;
  const focus = splitMinutes(tasks.focusMinutes);

  return (
    <>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <ActivityPanel data={data} />
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
          <StatTile
            index={0}
            icon={Users}
            tone="sky"
            label={t("dashboard.users.total")}
            value={<AnimatedNumber value={users.total} />}
            to="/users"
          />
          <StatTile
            index={1}
            icon={UserPlus}
            tone="mint"
            label={t("dashboard.users.new")}
            value={<AnimatedNumber value={users.new} />}
            aside={<DeltaBadge current={users.new} previous={previous.newUsers} />}
          />
          <StatTile
            index={2}
            icon={UserMinus}
            tone="amber"
            label={t("dashboard.users.atRisk")}
            value={<AnimatedNumber value={users.atRisk} />}
            hint={t("dashboard.users.atRiskHint", { count: data.activeDays })}
          />
          <StatTile
            index={3}
            icon={UserX}
            tone="violet"
            label={t("dashboard.users.never")}
            value={<AnimatedNumber value={users.neverActivated} />}
            hint={t("dashboard.users.neverHint")}
          />
          <StatTile
            index={4}
            icon={Send}
            tone="sky"
            label={t("dashboard.users.reachable")}
            value={<AnimatedNumber value={users.reachable} />}
            aside={
              <ProgressRing value={percent(users.reachable, users.total)} size={36} stroke={4} />
            }
          />
          <StatTile
            index={5}
            icon={Ban}
            tone={users.blocked > 0 ? "coral" : "neutral"}
            label={t("dashboard.users.blocked")}
            value={<AnimatedNumber value={users.blocked} />}
            to="/users?status=blocked"
          />
        </div>
      </div>

      <SeriesPanel data={data} />

      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 xl:grid-cols-6">
        <StatTile
          index={0}
          icon={ListPlus}
          tone="amber"
          label={t("dashboard.tasks.created")}
          value={<AnimatedNumber value={tasks.created} />}
          aside={<DeltaBadge current={tasks.created} previous={previous.tasksCreated} />}
        />
        <StatTile
          index={1}
          icon={CheckCircle2}
          tone="mint"
          label={t("dashboard.tasks.completed")}
          value={<AnimatedNumber value={tasks.completed} />}
          aside={<DeltaBadge current={tasks.completed} previous={previous.tasksCompleted} />}
        />
        <StatTile
          index={2}
          icon={Percent}
          tone={tasks.completionRate >= 60 ? "mint" : "amber"}
          label={t("dashboard.tasks.rate")}
          value={<AnimatedNumber value={tasks.completionRate} format={value => `${value}%`} />}
          hint={t("dashboard.tasks.rateHint", { count: tasks.planned })}
          aside={
            <DeltaBadge current={tasks.completionRate} previous={previous.completionRate} points />
          }
        />
        <StatTile
          index={3}
          icon={CalendarClock}
          tone="violet"
          label={t("dashboard.tasks.postponed")}
          value={<AnimatedNumber value={tasks.postponed} />}
        />
        <StatTile
          index={4}
          icon={XCircle}
          tone="coral"
          label={t("dashboard.tasks.cancelled")}
          value={<AnimatedNumber value={tasks.cancelled} />}
        />
        <StatTile
          index={5}
          icon={Timer}
          tone="sky"
          label={t("dashboard.tasks.focus")}
          value={t(focus.hours >= 100 ? "dashboard.hoursOnly" : "dashboard.hoursMinutes", focus)}
          hint={t("dashboard.tasks.focusHint")}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ClockPanel data={data} />
        <FunnelPanel data={data} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <TopUsersPanel data={data} />
        <GoalsReviewsPanel data={data} />
        <LanguagesPanel data={data} />
      </div>

      <DeliveriesPanel data={data} filters={filters} />
    </>
  );
}

/** Aktiv / aktivmas — halqa va DAU/WAU/MAU. Bo'laklar ro'yxatga olib boradi. */
function ActivityPanel({ data }: { data: AdminDashboard }) {
  const { t } = useTranslation();
  const { users, previous } = data;
  const activeShare = percent(users.active, users.total);
  const stickiness = percent(users.dau, users.mau);

  return (
    <Panel
      icon={Activity}
      tone="bg-tile-mint text-tile-mint-strong"
      title={t("dashboard.activity.title")}
      description={t("dashboard.activity.subtitle", { count: data.activeDays })}
      aside={<DeltaBadge current={users.active} previous={previous.activeUsers} />}
    >
      <div className="flex flex-wrap items-center gap-6">
        <Donut
          label={t("dashboard.activity.title")}
          parts={[
            { key: "active", value: users.active, className: "text-tile-mint-strong" },
            { key: "inactive", value: users.inactive, className: "text-tile-coral-strong/70" },
          ]}
        >
          <div>
            <p className="text-3xl font-semibold tabular-nums">{activeShare}%</p>
            <p className="text-muted-foreground text-xs">{t("dashboard.activity.activeShare")}</p>
          </div>
        </Donut>
        <div className="min-w-44 flex-1 space-y-2">
          <LegendLink
            to="/users?activity=active"
            dot="bg-tile-mint-strong"
            icon={UserCheck}
            label={t("dashboard.activity.active")}
            value={users.active}
          />
          <LegendLink
            to="/users?activity=inactive"
            dot="bg-tile-coral-strong/70"
            icon={UserMinus}
            label={t("dashboard.activity.inactive")}
            value={users.inactive}
          />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 border-t pt-4 text-center">
        {(
          [
            ["dau", users.dau],
            ["wau", users.wau],
            ["mau", users.mau],
          ] as const
        ).map(([key, value]) => (
          <div key={key}>
            <p className="text-lg font-semibold tabular-nums">{formatNumber(value)}</p>
            <p className="text-muted-foreground text-[11px] uppercase">
              {t(`dashboard.activity.${key}`)}
            </p>
          </div>
        ))}
        <div title={t("dashboard.activity.stickinessHint")}>
          <p className="text-primary text-lg font-semibold tabular-nums">{stickiness}%</p>
          <p className="text-muted-foreground text-[11px] uppercase">
            {t("dashboard.activity.stickiness")}
          </p>
        </div>
      </div>
    </Panel>
  );
}

function LegendLink({
  to,
  dot,
  icon: Icon,
  label,
  value,
}: {
  to: string;
  dot: string;
  icon: typeof Users;
  label: string;
  value: number;
}) {
  return (
    <Link
      to={to}
      className="group hover:bg-muted/70 -mx-2 flex items-center gap-3 rounded-xl px-2 py-2 transition-colors"
    >
      <span className={cn("size-2.5 rounded-full", dot)} />
      <Icon className="text-muted-foreground size-4" aria-hidden />
      <span className="flex-1 text-sm">{label}</span>
      <span className="font-semibold tabular-nums">{formatNumber(value)}</span>
      <ArrowRight
        className="text-muted-foreground size-3.5 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100"
        aria-hidden
      />
    </Link>
  );
}

type SeriesMetric = "activeUsers" | "signups" | "tasksCreated" | "tasksCompleted";
const SERIES_TONES: Record<SeriesMetric, string> = {
  activeUsers: "bg-tile-mint-strong",
  signups: "bg-tile-sky-strong",
  tasksCreated: "bg-tile-amber-strong",
  tasksCompleted: "bg-primary",
};

function SeriesPanel({ data }: { data: AdminDashboard }) {
  const { t } = useTranslation();
  const lang = currentLanguage();
  const [metric, setMetric] = useState<SeriesMetric>("activeUsers");
  const total = data.series.reduce((sum, point) => sum + point[metric], 0);
  const label = (point: SeriesPoint) =>
    data.bucket === "month"
      ? (formatShortDay(point.date, lang).split(" ")[1] ?? point.date)
      : formatShortDay(point.date, lang);

  return (
    <Panel
      icon={TrendingUp}
      tone="bg-tile-coral text-tile-coral-strong"
      title={t("dashboard.series.title")}
      description={t(`dashboard.series.bucket.${data.bucket}`)}
      aside={
        <div
          role="tablist"
          aria-label={t("dashboard.series.title")}
          className="flex flex-wrap gap-1"
        >
          {(Object.keys(SERIES_TONES) as SeriesMetric[]).map(key => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={metric === key}
              onClick={() => setMetric(key)}
              className={cn(
                "relative rounded-full px-3 py-1 text-xs font-medium transition-colors",
                metric === key ? "text-card" : "text-muted-foreground hover:bg-muted",
              )}
            >
              {metric === key ? (
                <motion.span
                  layoutId="series-metric"
                  className={cn("absolute inset-0 rounded-full", SERIES_TONES[key])}
                  transition={{ type: "spring", stiffness: 420, damping: 32 }}
                />
              ) : null}
              <span className="relative">{t(`dashboard.series.${key}`)}</span>
            </button>
          ))}
        </div>
      }
    >
      <p className="text-3xl font-semibold tabular-nums">
        {formatNumber(total)}
        <span className="text-muted-foreground ml-2 text-sm font-normal">
          {t("dashboard.series.inPeriod")}
        </span>
      </p>
      <TrendChart
        label={t(`dashboard.series.${metric}`)}
        tone={SERIES_TONES[metric]}
        points={data.series.map(point => ({
          key: point.date,
          label: label(point),
          value: point[metric],
        }))}
      />
    </Panel>
  );
}

function ClockPanel({ data }: { data: AdminDashboard }) {
  const { t } = useTranslation();
  const hours = Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, "0"));
  const weekdays = t("dashboard.weekdays").split(",");
  const peakHour = data.completionsByHour.indexOf(Math.max(...data.completionsByHour));
  const empty = data.completionsByHour.every(value => value === 0);

  return (
    <Panel
      icon={Clock}
      tone="bg-tile-sky text-tile-sky-strong"
      title={t("dashboard.clock.title")}
      description={
        empty ? t("dashboard.clock.empty") : t("dashboard.clock.peak", { hour: hours[peakHour] })
      }
      delay={0.05}
    >
      <StripChart
        label={t("dashboard.clock.hours")}
        values={data.completionsByHour}
        labels={hours}
        every={3}
      />
      <StripChart
        label={t("dashboard.clock.weekdays")}
        values={data.completionsByWeekday}
        labels={weekdays}
      />
    </Panel>
  );
}

/** Faollashuv voronkasi: kelganlarning qanchasi ilovadan haqiqatan foydalandi. */
function FunnelPanel({ data }: { data: AdminDashboard }) {
  const { t } = useTranslation();
  const steps = funnelSteps(data.funnel);
  return (
    <Panel
      icon={Filter}
      tone="bg-tile-violet text-tile-violet-strong"
      title={t("dashboard.funnel.title")}
      description={t("dashboard.funnel.subtitle")}
      delay={0.1}
    >
      {data.funnel.registered === 0 ? (
        <p className="text-muted-foreground py-6 text-center text-sm">
          {t("dashboard.funnel.empty")}
        </p>
      ) : (
        <ol className="space-y-2.5">
          {steps.map((step, index) => (
            <li key={step.key} className="space-y-1">
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span>{t(`dashboard.funnel.${step.key}`)}</span>
                <span className="tabular-nums">
                  <b>{formatNumber(step.value)}</b>
                  <span className="text-muted-foreground ml-1.5 text-xs">{step.share}%</span>
                </span>
              </div>
              <div className="bg-muted h-3 overflow-hidden rounded-full">
                <motion.div
                  className="from-tile-violet-strong to-primary h-full rounded-full bg-gradient-to-r"
                  initial={{ width: 0 }}
                  animate={{ width: `${step.share}%` }}
                  transition={{ duration: 0.8, delay: 0.2 + index * 0.12, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
}

function TopUsersPanel({ data }: { data: AdminDashboard }) {
  const { t } = useTranslation();
  return (
    <Panel
      icon={Trophy}
      tone="bg-tile-amber text-tile-amber-strong"
      title={t("dashboard.top.title")}
      description={t("dashboard.top.subtitle")}
      delay={0.05}
    >
      {data.topUsers.length === 0 ? (
        <p className="text-muted-foreground py-6 text-center text-sm">{t("dashboard.top.empty")}</p>
      ) : (
        <ol className="space-y-1">
          {data.topUsers.map((user, index) => {
            const name = displayName(user);
            return (
              <motion.li
                key={user.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 + index * 0.06 }}
              >
                <Link
                  to={`/users/${user.id}`}
                  className="hover:bg-muted/70 -mx-2 flex items-center gap-3 rounded-xl px-2 py-1.5 transition-colors"
                >
                  <span
                    className={cn(
                      "w-5 text-center text-sm font-bold tabular-nums",
                      index === 0 ? "text-tile-amber-strong" : "text-muted-foreground",
                    )}
                  >
                    {index + 1}
                  </span>
                  <UserAvatar name={name} seed={user.id} className="size-8" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{name}</span>
                  <span className="text-sm tabular-nums">
                    <b>{user.completed}</b>
                    <span className="text-muted-foreground">/{user.planned}</span>
                  </span>
                </Link>
              </motion.li>
            );
          })}
        </ol>
      )}
    </Panel>
  );
}

function GoalsReviewsPanel({ data }: { data: AdminDashboard }) {
  const { t } = useTranslation();
  const rows: [typeof Target, string, number][] = [
    [Target, t("dashboard.goals.created"), data.goals.created],
    [Flag, t("dashboard.goals.completed"), data.goals.completed],
    [Target, t("dashboard.goals.active"), data.goals.active],
    [BookOpenCheck, t("dashboard.reviews.daily"), data.reviews.daily],
    [BookOpenCheck, t("dashboard.reviews.weekly"), data.reviews.weekly],
    [BookOpenCheck, t("dashboard.reviews.period"), data.reviews.period],
  ];
  return (
    <Panel
      icon={Target}
      tone="bg-tile-coral text-tile-coral-strong"
      title={t("dashboard.goals.title")}
      delay={0.1}
    >
      <dl className="grid grid-cols-2 gap-2">
        {rows.map(([Icon, label, value]) => (
          <div key={label} className="bg-muted/50 rounded-xl p-2.5">
            <dt className="text-muted-foreground flex items-center gap-1.5 text-xs">
              <Icon className="size-3.5" aria-hidden />
              {label}
            </dt>
            <dd className="mt-0.5 text-xl font-semibold tabular-nums">{formatNumber(value)}</dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
}

const LANGUAGE_LABELS: Record<string, string> = { uz: "O'zbekcha", ru: "Русский", en: "English" };
const LANGUAGE_TONES = ["bg-tile-sky-strong", "bg-tile-coral-strong", "bg-tile-mint-strong"];

function LanguagesPanel({ data }: { data: AdminDashboard }) {
  const { t } = useTranslation();
  const total = data.languages.reduce((sum, row) => sum + row.count, 0);
  return (
    <Panel
      icon={Languages}
      tone="bg-tile-sky text-tile-sky-strong"
      title={t("dashboard.languages.title")}
      description={t("dashboard.languages.subtitle")}
      delay={0.15}
    >
      <div className="bg-muted flex h-3 overflow-hidden rounded-full">
        {data.languages.map((row, index) => (
          <motion.div
            key={row.language}
            className={LANGUAGE_TONES[index % LANGUAGE_TONES.length]}
            initial={{ width: 0 }}
            animate={{ width: `${percent(row.count, total)}%` }}
            transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
          />
        ))}
      </div>
      <ul className="space-y-1.5">
        {data.languages.map((row, index) => (
          <li key={row.language} className="flex items-center gap-2 text-sm">
            <span
              className={cn("size-2.5 rounded-full", LANGUAGE_TONES[index % LANGUAGE_TONES.length])}
            />
            <span className="flex-1">{LANGUAGE_LABELS[row.language] ?? row.language}</span>
            <b className="tabular-nums">{formatNumber(row.count)}</b>
            <span className="text-muted-foreground w-10 text-right text-xs tabular-nums">
              {percent(row.count, total)}%
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function DeliveriesPanel({ data, filters }: { data: AdminDashboard; filters: DashboardParams }) {
  const { t } = useTranslation();
  const channels = summarizeDeliveries(
    data.deliveries,
    filters.channel ? [filters.channel] : ["TELEGRAM", "WEB"],
  );
  return (
    <Panel
      icon={Send}
      tone="bg-tile-violet text-tile-violet-strong"
      title={t("dashboard.deliveries")}
    >
      <div className="grid gap-5 md:grid-cols-2">
        {channels.map(channel => (
          <ChannelRow key={channel.channel} summary={channel} />
        ))}
      </div>
    </Panel>
  );
}

function ChannelRow({ summary }: { summary: ChannelSummary }) {
  const { t } = useTranslation();
  const { total, delivered, failed, pending } = summary;
  const width = (n: number) => `${total === 0 ? 0 : (n / total) * 100}%`;
  const telegram = summary.channel === "TELEGRAM";
  const Icon = telegram ? Send : Globe;

  return (
    <div className="flex gap-4">
      <ProgressRing
        value={total > 0 ? summary.successRate : 0}
        size={56}
        stroke={6}
        className={telegram ? "text-tile-sky-strong" : "text-tile-violet-strong"}
      >
        <Icon className="size-4" aria-hidden />
      </ProgressRing>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-baseline justify-between gap-2 text-sm">
          <span className="font-semibold">{t(`deliveries.channels.${summary.channel}`)}</span>
          {total > 0 ? (
            <span className="text-muted-foreground text-xs">
              {t("overview.successRate", { percent: summary.successRate })}
            </span>
          ) : null}
        </div>
        {total === 0 ? (
          <p className="text-muted-foreground text-xs">{t("overview.noDeliveries")}</p>
        ) : (
          <>
            <div className="bg-muted flex h-2 overflow-hidden rounded-full">
              {[
                { key: "ok", value: delivered, className: "bg-success" },
                { key: "wait", value: pending, className: "bg-warning" },
                { key: "fail", value: failed, className: "bg-destructive" },
              ].map((part, index) => (
                <motion.div
                  key={part.key}
                  className={part.className}
                  initial={{ width: 0 }}
                  animate={{ width: width(part.value) }}
                  transition={{ duration: 0.8, delay: 0.3 + index * 0.1, ease: [0.16, 1, 0.3, 1] }}
                />
              ))}
            </div>
            <div className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 text-xs">
              <span>
                {t("overview.delivered")}: <b className="text-foreground">{delivered}</b>
              </span>
              <span>
                {t("overview.pending")}: <b className="text-foreground">{pending}</b>
              </span>
              <span>
                {t("overview.failed")}: <b className="text-foreground">{failed}</b>
              </span>
            </div>
            {failed > 0 ? (
              <Link
                to={`/deliveries?status=FAILED&channel=${summary.channel}`}
                className="text-primary group inline-flex items-center gap-1 text-xs font-medium"
              >
                {t("overview.failedLink")}
                <ArrowRight
                  className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                />
              </Link>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
